import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (n) =>
  `€${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStartISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export default function VentasMymPanel() {
  const [start, setStart] = useState(monthStartISO());
  const [end, setEnd] = useState(todayISO());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatsRange = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/dashboard/mym-stats?start_date=${start}&end_date=${end}`,
        { withCredentials: true }
      );
      setStats(r.data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchStatsRange();
  }, [fetchStatsRange]);

  return (
    <div className="space-y-6">
      {/* Header + filtros */}
      <Card
        data-testid="ventas-mym-panel"
        className="bg-[#0a0a0a] border border-pink-500/30 rounded-2xl"
      >
        <CardHeader>
          <div className="flex flex-wrap items-end gap-4 justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-white text-xl">Ventas MYM Fans</CardTitle>
                <p className="text-xs text-gray-400 mt-0.5">
                  Reportes de MYM Fans agregados (€) — independientes de OnlyFans.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-gray-400 text-xs">Desde</Label>
                <Input
                  data-testid="mym-filter-start"
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white h-9 w-[150px]"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Hasta</Label>
                <Input
                  data-testid="mym-filter-end"
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white h-9 w-[150px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Total */}
          <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/5 border border-pink-500/40 rounded-xl p-5 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Total MYM en periodo</div>
              <div className="text-[11px] text-gray-500">{stats?.total_reports || 0} reporte(s) con ventas MYM</div>
            </div>
            <div
              data-testid="mym-total-eur"
              className="text-4xl font-bold text-pink-300"
            >
              {loading ? "…" : fmtEur(stats?.total_eur)}
            </div>
          </div>

          {/* By model */}
          <div>
            <h3 className="text-white font-semibold mb-3">Por Modelo</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(stats?.by_model || []).length === 0 && (
                <div className="text-gray-500 text-sm">Sin ventas MYM en este periodo.</div>
              )}
              {(stats?.by_model || []).map((m) => (
                <div
                  key={m.modelo_id}
                  data-testid={`mym-model-row-${m.modelo_id}`}
                  className="bg-zinc-900/50 border border-pink-500/20 rounded-xl p-3 flex items-center gap-3"
                >
                  {m.photo_url ? (
                    <img
                      src={m.photo_url}
                      alt={m.modelo_name}
                      className="w-10 h-10 rounded-full object-cover border border-pink-500/40"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white font-bold">
                      {(m.modelo_name || "?")[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium truncate">{m.modelo_name}</div>
                    <div className="text-pink-300 text-base font-bold">{fmtEur(m.total_eur)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By chatter */}
          <div>
            <h3 className="text-white font-semibold mb-3">Por Chatter</h3>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl divide-y divide-zinc-800">
              {(stats?.by_chatter || []).length === 0 && (
                <div className="text-gray-500 text-sm p-3">Sin datos.</div>
              )}
              {(stats?.by_chatter || []).map((c) => (
                <div key={c.user_id} className="flex items-center justify-between p-3">
                  <div className="text-white text-sm">{c.user_name}</div>
                  <div className="text-pink-300 font-bold">{fmtEur(c.total_eur)}</div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
